import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { FiArrowRight, FiX } from 'react-icons/fi';
import { ff } from '../../theme';

export type ModalShellTone = 'ink' | 'danger' | 'lime';

export interface ModalShellPrimary {
  label: string;
  onClick: () => void;
  tone?: ModalShellTone;
  arrow?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  isDisabled?: boolean;
}

export interface ModalShellHeader {
  name: string;
  subtitle?: string;
  imageSrc?: string;
}

export interface ModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  header?: ModalShellHeader;
  children?: React.ReactNode;
  primary?: ModalShellPrimary;
  /** Outlined Cancel next to the primary; omit to hide it. */
  cancelLabel?: string;
  onCancel?: () => void;
  /** Disables Cancel (e.g. while a request runs). */
  isCancelDisabled?: boolean;
  /** Replaces the default footer entirely. */
  footer?: React.ReactNode;
  hideClose?: boolean;
  size?: string;
  scrollBehavior?: 'inside' | 'outside';
  closeOnOverlayClick?: boolean;
}

const toneVariant: Record<ModalShellTone, string> = { ink: 'ink', danger: 'danger', lime: 'lime' };

const ModalShell: React.FC<ModalShellProps> = ({
  isOpen,
  onClose,
  title,
  header,
  children,
  primary,
  cancelLabel,
  onCancel,
  isCancelDisabled,
  footer,
  hideClose,
  size = 'md',
  scrollBehavior,
  closeOnOverlayClick,
}) => {
  const tone = primary?.tone ?? 'ink';
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      motionPreset="slideInBottom"
      scrollBehavior={scrollBehavior}
      closeOnOverlayClick={closeOnOverlayClick}
    >
      <ModalOverlay />
      <ModalContent
        fontFamily="display"
        // Bottom sheet on mobile, centered card from md up — CSS only, no media-query hook.
        borderTopRadius="24px"
        borderBottomRadius={{ base: 0, md: '24px' }}
        mx={{ base: 0, md: 4 }}
        my={{ base: 0, md: 'auto' }}
        mt={{ base: 'auto', md: 'auto' }}
        maxW={{ base: '100%', md: undefined }}
        // Never taller than the visible viewport (dvh tracks mobile browser bars): the body scrolls,
        // so the footer button stays on screen instead of sliding under the toolbar.
        maxH={{ base: 'calc(100dvh - 12px)', md: 'calc(100dvh - 64px)' }}
        display="flex"
        flexDirection="column"
        p={5}
        pb={{ base: 'calc(20px + env(safe-area-inset-bottom))', md: 5 }}
        shadow="xl"
      >
        {(header || !hideClose) && (
          <Flex align="center" gap={3} mb={header ? 4 : 0} minH="32px" flexShrink={0}>
            {header && (
              <>
                <Avatar size="sm" name={header.name} src={header.imageSrc} />
                <Box minW={0} flex={1}>
                  <Text fontWeight="600" fontSize="14px" noOfLines={1}>{header.name}</Text>
                  {header.subtitle && (
                    <Text fontSize="12px" color={ff.text2} noOfLines={1}>{header.subtitle}</Text>
                  )}
                </Box>
              </>
            )}
            {!header && <Box flex={1} />}
            {!hideClose && (
              <IconButton
                aria-label="Close"
                icon={<FiX />}
                variant="ghost"
                size="sm"
                borderRadius="full"
                onClick={onClose}
              />
            )}
          </Flex>
        )}
        {title && (
          <Text as="h2" fontSize="24px" fontWeight="600" fontFamily="display" lineHeight={1.15} letterSpacing="-0.02em" mb={3} flexShrink={0}>
            {title}
          </Text>
        )}
        <Box flex="1" minH={0} overflowY="auto" mx={-5} px={5}>{children}</Box>
        {footer ??
          (primary || cancelLabel ? (
            <Flex gap={3} mt={5} flexShrink={0}>
              {cancelLabel && (
                <Button variant="inkOutline" onClick={onCancel ?? onClose} isDisabled={isCancelDisabled}>
                  {cancelLabel}
                </Button>
              )}
              {primary && (
                <Button
                  variant={toneVariant[tone]}
                  flex={1}
                  onClick={primary.onClick}
                  isLoading={primary.isLoading}
                  loadingText={primary.loadingText}
                  isDisabled={primary.isDisabled}
                  rightIcon={primary.arrow ? <FiArrowRight /> : undefined}
                >
                  {primary.label}
                </Button>
              )}
            </Flex>
          ) : null)}
      </ModalContent>
    </Modal>
  );
};

export default ModalShell;
